import * as React from "react"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"

type ToastVariant = "default" | "destructive" | "success" | "warning"

interface Toast {
  id: string
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, "id">) => void
  removeToast: (id: string) => void
}

const ToastContext = React.createContext<ToastContextType | null>(null)

export const useToast = () => {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within ToastProvider")
  }
  return context
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const addToast = React.useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast = { ...toast, id }
    
    // Clear existing toasts before adding new one
    setToasts([newToast])

    const duration = toast.duration || 5000
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }, [])

  const removeToast = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

const ToastContainer = () => {
  const { toasts } = useToast()

  return (
    <div className="fixed bottom-36 left-1/2 -translate-x-1/2 z-[100] flex max-h-screen flex-col-reverse items-center gap-2 px-4">
      {toasts.map((toast) => (
        <ToastComponent key={toast.id} toast={toast} />
      ))}
    </div>
  )
}

interface ToastComponentProps {
  toast: Toast
}

const ToastComponent = ({ toast }: ToastComponentProps) => {
  const { removeToast } = useToast()

  const variantClasses = {
    default: "border bg-background text-foreground",
    destructive: "border-destructive bg-destructive text-destructive-foreground",
    success: "border-green-500 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-50",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-50"
  }

  return (
    <div
      className={cn(
        "group pointer-events-auto relative flex items-center justify-between space-x-2 overflow-hidden rounded-lg border px-4 py-3 shadow-lg transition-all animate-slide-in w-[85vw] sm:w-fit sm:min-w-[320px] sm:max-w-lg",
        variantClasses[toast.variant || "default"]
      )}
    >
      <div className="flex-1 text-center">
        <div className="text-xs font-medium leading-tight line-clamp-2">
          {toast.title}
        </div>
      </div>
      <button
        className="flex-shrink-0 rounded-full p-1 text-foreground/50 transition-opacity hover:text-foreground focus:outline-none"
        onClick={() => removeToast(toast.id)}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}

export { ToastComponent as Toast }